import type { EntitySummary } from '../summary';
import type { GameEvent } from '../types';
import type { DiffContext } from './context';

/** An own spy found out where it was disguised (urgent: the screen edge glows). */
export function ownSpyExposed(
  c: DiffContext,
  u: EntitySummary,
  before: EntitySummary
): GameEvent | null {
  if (!u.exposed || before.exposed) return null;
  const city = c.prev.buildings.get(before.disguiseCityId ?? '');
  return {
    kind: 'spy',
    text: city
      ? `${c.unitName(u.type)} exposed in ${c.playerName(city.owner)}'s city`
      : `${c.unitName(u.type)} exposed`,
    pos: u.pos,
    urgent: true,
  };
}

/**
 * Rival spies the player exposed: the state is the viewer's (viewerState.ts),
 * so until now each was one of their own units.
 */
export function rivalSpiesExposed(c: DiffContext): GameEvent[] {
  const events: GameEvent[] = [];
  for (const [id, u] of c.next.units) {
    if (u.owner === c.playerId || !u.exposed || c.prev.units.get(id)?.owner !== c.playerId) {
      continue;
    }
    events.push({
      kind: 'spy',
      text: `${c.unitName(u.type)} exposed!`,
      pos: u.pos,
      target: { id, pos: { ...u.pos }, label: `${c.playerName(u.owner)}'s ${c.unitName(u.type)}` },
    });
  }
  return events;
}

/**
 * Rival spies caught lurking in the player's territory (spyGrace): hidden
 * from them until now, or seen undisguised before they were caught.
 */
export function spiesCaught(c: DiffContext): GameEvent[] {
  const events: GameEvent[] = [];
  for (const [id, u] of c.next.units) {
    if (u.owner === c.playerId || !u.caughtIn) continue;
    const before = c.prev.units.get(id);
    if (before?.exposed || before?.owner === c.playerId) continue;
    events.push({
      kind: 'spy',
      text: `${c.unitName(u.type)} caught in ${c.cityLabel?.(u.caughtIn) ?? 'your territory'}`,
      pos: u.pos,
      target: { id, pos: { ...u.pos }, label: `${c.playerName(u.owner)}'s ${c.unitName(u.type)}` },
    });
  }
  return events;
}
